import { toast as hotToast } from 'react-hot-toast'
import { QueryClient } from 'react-query'
import apiClient from '../../utils/api'
import { placeRoutineViewAfterRoutines, removeOverviewView } from '../../utils/overview'
import { TOverviewView } from '../../utils/types'
import { toast } from '../molecules/toast/utils'

export interface TRoutineCreatedResponse {
    id: string
    /** Server-generated step ids in request order, used to release queued edits without a refetch. */
    item_ids?: string[]
    overview_placement?: 'placed' | 'failed'
    overview_view_id?: string
}

const RECEIPT_DURATION = 10000

// The receipt after a routine is created or duplicated. The server places the routine's card after
// the last routine card in the same request; a response without placement metadata completes the
// placement from the client. Both paths offer Undo after placement or Retry when it failed.
// Undo and Retry call the API directly rather than through mutation hooks: the editor that started
// the create has already closed, and a hook owned by an unmounted component drops its callbacks.
export const showRoutinePlacementReceipt = (
    queryClient: QueryClient,
    name: string,
    routineId: string,
    response: TRoutineCreatedResponse
) => {
    // One toast per routine, replaced in place as it moves between placed, retrying and failed.
    const toastId = `routine-placement-${routineId}`
    const refresh = () => {
        queryClient.invalidateQueries('overview')
        queryClient.invalidateQueries('overview-supported-views')
        queryClient.invalidateQueries('routines')
    }

    // Show the card in place right away instead of after the debounced post-mutation refetch. It
    // carries no status or steps yet, so it shows no status chip rather than a guessed one; the
    // refetch fills it in.
    const showCard = (viewId: string) => {
        const views = queryClient.getQueryData<TOverviewView[]>('overview')
        if (views && !views.some((view) => view.id === viewId)) {
            const card: TOverviewView = {
                id: viewId,
                name,
                type: 'routine',
                routine_id: routineId,
                is_reorderable: false,
                logo: 'generaltask',
                view_items: [],
                view_item_ids: [],
                sources: [],
                is_linked: true,
                has_tasks_completed_today: false,
            }
            queryClient.setQueryData('overview', placeRoutineViewAfterRoutines(views, card))
        }
        refresh()
    }

    const showPlaced = (viewId: string) =>
        toast(`${name} added to Daily Overview`, {
            toastId,
            duration: RECEIPT_DURATION,
            actions: { value: 'Undo', styleType: 'secondary', onClick: () => undo(viewId) },
        })

    const showFailed = () =>
        toast(`Couldn't add ${name} to Daily Overview`, {
            toastId,
            type: 'error',
            duration: Infinity,
            ariaProps: { role: 'alert', 'aria-live': 'assertive' },
            actions: { value: 'Retry', styleType: 'secondary', onClick: retry },
        })

    const undo = async (viewId: string) => {
        hotToast.dismiss(toastId)
        const views = queryClient.getQueryData<TOverviewView[]>('overview')
        if (views) queryClient.setQueryData('overview', removeOverviewView(views, viewId))
        try {
            await apiClient.delete(`/overview/views/${viewId}/`)
            toast(`${name} removed from Daily Overview`, { toastId, duration: 4000 })
        } catch {
            if (views) queryClient.setQueryData('overview', views)
            toast(`Couldn't remove ${name} from Daily Overview`, {
                toastId,
                type: 'error',
                ariaProps: { role: 'alert', 'aria-live': 'assertive' },
            })
        } finally {
            refresh()
        }
    }

    // Idempotent on the server: a placement that did land returns the existing card, and the add
    // endpoint places a routine card after the last routine card, the same as routine creation.
    const retry = async () => {
        toast(`Adding ${name} to Daily Overview…`, { toastId, type: 'loading' })
        try {
            const res = await apiClient.post('/overview/views/', { type: 'routine', routine_id: routineId })
            const viewId: string | undefined = res.data.id
            if (!viewId) throw new Error('placement response did not include a view ID')
            showCard(viewId)
            showPlaced(viewId)
        } catch {
            showFailed()
        } finally {
            refresh()
        }
    }

    if (response.overview_placement === 'placed' && response.overview_view_id) {
        showCard(response.overview_view_id)
        showPlaced(response.overview_view_id)
    } else if (response.overview_placement === 'failed') showFailed()
    else return retry()
}
